/* Config-driven field definitions for the Loyalty & Benefits Wallet.
   Field: { k key, l label, t type(text|number|date|select|textarea|yesno|list), opts, ph, full } */

export const COVERAGE_OPTIONS = [
  ["", "Who is covered?"],
  ["traveler_only", "Traveler only"],
  ["plus_one", "Traveler + 1 companion"],
  ["multiple_companions", "Traveler + multiple companions"],
  ["same_reservation", "Everyone on same reservation"],
  ["authorized_users", "Authorized users"],
  ["spouse_partner", "Spouse / partner"],
  ["family", "Family members"],
  ["household", "Household members"],
  ["other", "Other"],
];

export const BENEFIT_CATEGORIES = [
  ["companion", "Companion & family"],
  ["lounge", "Lounge access"],
  ["baggage", "Baggage"],
  ["seat", "Seats"],
  ["upgrade", "Upgrades"],
  ["priority", "Priority airport services"],
  ["flexibility", "Ticketing & flexibility"],
  ["onboard", "Onboard"],
  ["partner", "Alliance & partner"],
  ["other", "Other"],
];

export const CERT_TYPES = ["Companion Certificate", "Companion Fare", "Upgrade Certificate",
  "Lounge Certificate / Pass", "Travel Credit", "Flight Credit", "Voucher",
  "Free Baggage Certificate", "Seat Upgrade Certificate", "Hotel Certificate",
  "Rental Car Certificate", "Other"];

const airline = { k: "airline", l: "Airline / issuer", t: "text", ph: "e.g. Alaska Airlines" };
const traveler = { k: "traveler_name", l: "Traveler / owner", t: "text", ph: "Who owns this?" };
const coverage = { k: "coverage", l: "Coverage", t: "select", opts: COVERAGE_OPTIONS };
const expiry = { k: "expiration_date", l: "Expiration date", t: "date" };
const url = { k: "url", l: "Redemption / info link", t: "text", ph: "https://…" };
const notes = { k: "notes", l: "Notes", t: "textarea", full: true };

export const KIND_CONFIG = {
  benefit: {
    label: "Benefit", plural: "Benefits", color: "text-emerald-700", border: "border-emerald-300",
    desc: "Companion, lounge, baggage, seat, upgrade, priority, onboard and alliance/partner benefits.",
    main: [
      { k: "name", l: "Benefit name", t: "text", ph: "e.g. Free first checked bag" },
      { k: "category", l: "Category", t: "select", opts: [["", "Category…"], ...BENEFIT_CATEGORIES] },
      airline, { k: "source", l: "Source", t: "select", opts: [["", "Where does it come from?"], ["status", "Elite status"], ["membership", "Paid membership"], ["credit_card", "Credit card"], ["alliance", "Alliance / partner"], ["promotion", "Promotion"], ["other", "Other"]] },
      coverage, expiry,
    ],
    more: [
      { k: "alliance", l: "Alliance", t: "select", opts: [["", "Alliance (if partner benefit)"], ["Star Alliance", "Star Alliance"], ["oneworld", "oneworld"], ["SkyTeam", "SkyTeam"]] },
      { k: "visits_total", l: "Visits / uses total", t: "number" },
      { k: "visits_remaining", l: "Visits / uses remaining", t: "number" },
      { k: "guest_allowance", l: "Guest allowance", t: "text", ph: "e.g. 1 free guest" },
      { k: "airports", l: "Airports / lounges", t: "text", ph: "e.g. SEA, PDX" },
      { k: "eligible_routes", l: "Eligible routes", t: "text", ph: "e.g. domestic US only" },
      { k: "eligible_cabins", l: "Eligible cabins", t: "text" },
      { k: "renewal_date", l: "Renewal date", t: "date" },
      traveler, url,
      { k: "restrictions", l: "Restrictions", t: "textarea", full: true },
      notes,
    ],
  },
  card: {
    label: "Credit card", plural: "Credit Card Benefits", color: "text-violet-700", border: "border-violet-300",
    desc: "Airline & travel credit cards. Only the last 4 digits — never full numbers, CVV or PINs.",
    main: [
      { k: "card_name", l: "Card name", t: "text", ph: "e.g. Alaska Visa Signature" },
      { k: "issuer", l: "Issuing bank", t: "text", ph: "e.g. Bank of America" },
      airline,
      { k: "cardholder", l: "Cardholder", t: "text" },
      { k: "last4", l: "Last 4 digits only", t: "text", ph: "4827" },
      { k: "annual_fee", l: "Annual fee", t: "text", ph: "$95" },
    ],
    more: [
      { k: "renewal_date", l: "Renewal / anniversary date", t: "date" },
      { k: "program_linked", l: "Loyalty program linked", t: "text" },
      { k: "rewards_number", l: "Rewards number (masked)", t: "text" },
      { k: "benefits", l: "Benefits (one per line)", t: "list", full: true, ph: "Free checked bag\nCompanion certificate\nLounge passes\nTSA PreCheck credit" },
      url, notes,
    ],
  },
  certificate: {
    label: "Certificate", plural: "Certificates", color: "text-blue-700", border: "border-blue-300",
    desc: "Companion, upgrade, lounge, baggage and other certificates & vouchers.",
    main: [
      { k: "name", l: "Certificate name", t: "text", ph: "e.g. Companion Fare" },
      { k: "cert_type", l: "Type", t: "select", opts: [["", "Type…"], ...CERT_TYPES.map((c) => [c, c])] },
      airline,
      { k: "number", l: "Certificate number / code (masked)", t: "text" },
      expiry,
      { k: "value", l: "Monetary value", t: "number", ph: "e.g. 122" },
    ],
    more: [
      { k: "currency", l: "Currency", t: "text", ph: "USD" },
      { k: "issued_to", l: "Issued to", t: "text" },
      { k: "issue_date", l: "Issue date", t: "date" },
      { k: "min_purchase", l: "Minimum purchase", t: "text" },
      { k: "eligible_routes", l: "Eligible routes / destinations", t: "text" },
      { k: "eligible_cabins", l: "Eligible cabins / fare classes", t: "text" },
      { k: "companion_eligible", l: "Companion eligible?", t: "yesno" },
      coverage, { k: "booking_ref", l: "Reservation / confirmation no.", t: "text" },
      traveler, url,
      { k: "terms", l: "Terms", t: "textarea", full: true },
      { k: "restrictions", l: "Restrictions", t: "textarea", full: true },
      notes,
    ],
  },
  credit: {
    label: "Travel credit", plural: "Travel Credits", color: "text-teal-700", border: "border-teal-300",
    desc: "Travel & flight credits — partial use supported, remaining balance tracked.",
    main: [
      airline,
      { k: "credit_type", l: "Credit type", t: "select", opts: [["", "Type…"], ["Flight credit", "Flight credit"], ["Travel credit", "Travel credit"], ["eCredit", "eCredit"], ["Voucher", "Voucher"], ["Incidental credit", "Incidental credit"], ["Other", "Other"]] },
      { k: "original_value", l: "Original value", t: "number" },
      { k: "currency", l: "Currency", t: "text", ph: "USD" },
      expiry,
      { k: "number", l: "Credit / voucher number (masked)", t: "text" },
    ],
    more: [
      { k: "original_ticket", l: "Original ticket number", t: "text" },
      { k: "issue_date", l: "Issue date", t: "date" },
      { k: "eligible_traveler", l: "Eligible traveler", t: "text" },
      { k: "transferable", l: "Transferable?", t: "yesno" },
      { k: "eligible_routes", l: "Eligible routes", t: "text" },
      { k: "related_flight", l: "Related cancelled/changed flight", t: "text" },
      traveler, url,
      { k: "restrictions", l: "Restrictions", t: "textarea", full: true },
      notes,
    ],
  },
  code: {
    label: "Discount code", plural: "Discount Codes", color: "text-orange-700", border: "border-orange-300",
    desc: "Promo & discount codes with booking deadlines — copy with one tap.",
    main: [
      { k: "promo_name", l: "Promotion name", t: "text", ph: "e.g. Spring Sale 20% off" },
      { k: "code", l: "Code", t: "text", ph: "SPRING20" },
      airline,
      { k: "promo_type", l: "Type", t: "select", opts: [["", "Type…"], ["percent", "Percentage off"], ["amount", "Dollar amount"], ["bonus_miles", "Bonus miles"], ["companion", "Companion"], ["upgrade", "Upgrade"], ["baggage", "Free baggage"], ["lounge", "Lounge"], ["other", "Other"]] },
      { k: "booking_deadline", l: "Booking deadline", t: "date" },
      expiry,
    ],
    more: [
      { k: "percent", l: "Discount %", t: "number" },
      { k: "amount", l: "Discount amount", t: "number" },
      { k: "travel_dates", l: "Eligible travel dates", t: "text" },
      { k: "eligible_travelers", l: "Eligible travelers", t: "text" },
      { k: "eligible_routes", l: "Eligible routes / destinations", t: "text" },
      { k: "min_purchase", l: "Minimum purchase", t: "text" },
      { k: "uses_allowed", l: "Uses allowed", t: "number" },
      { k: "uses_remaining", l: "Uses remaining", t: "number" },
      { k: "source", l: "Source of code", t: "text", ph: "e.g. email promo" },
      url,
      { k: "terms", l: "Terms", t: "textarea", full: true },
      { k: "restrictions", l: "Restrictions", t: "textarea", full: true },
      notes,
    ],
  },
  pool: {
    label: "Mileage pool", plural: "Miles Pooling", color: "text-rose-700", border: "border-rose-300",
    desc: "Family / household mileage pools with members and a permanent transaction ledger.",
    main: [
      airline,
      { k: "program", l: "Loyalty program", t: "text", ph: "e.g. Atmos Rewards" },
      { k: "pool_name", l: "Pool / family account name", t: "text" },
      { k: "admin_name", l: "Pool administrator", t: "text" },
      { k: "admin_number", l: "Administrator rewards no. (masked)", t: "text" },
      { k: "available_miles", l: "Available pooled miles/points", t: "number" },
    ],
    more: [
      { k: "unit", l: "Unit", t: "select", opts: [["miles", "miles"], ["points", "points"]] },
      { k: "total_miles", l: "Total pooled (lifetime)", t: "number" },
      { k: "pending_miles", l: "Pending", t: "number" },
      { k: "used_miles", l: "Used", t: "number" },
      { k: "created_date", l: "Pool creation date", t: "date" },
      { k: "rules_url", l: "Program rules URL", t: "text" },
      expiry, notes,
    ],
  },
};

export const kindTitle = (kind, it) =>
  it.name || it.pool_name || it.card_name || it.promo_name || it.credit_type || it.cert_type ||
  it.airline || KIND_CONFIG[kind].label;
